import type { FormatMessage } from '@tmapy/intl';

import type { OrderListResponse } from '../openapi/types/OrderList';
import type { ServiceOrderItemResponse } from '../openapi/types/ServiceOrderItem';
import type { ServiceOrderItemEditRequest } from '../openapi/types/ServiceOrderItemEdit';
import { priceListMessages } from '../priceList/components/priceListMessages';
import { formatQuantityWithUnit, isDataPriceListItemType } from '../priceList/utils';
import type { ServerError } from '../types';

import { serviceOrderMessages } from './components/serviceOrderMessages';

/** Uložený řádek zná z navázané objednávky jen `id`, výdejní sady má jen záznam z nabídky. */
export type DataOrderValue = Pick<OrderListResponse, 'id'> &
  Partial<Pick<OrderListResponse, 'publishsets'>>;

/** Název jako v přehledu objednávek: první výdejní sada a počet dalších. */
export const getDataOrderName = (order: DataOrderValue): string | null => {
  const [first, ...rest] = order.publishsets?.map((publishset) => publishset.publishset_name) ?? [];
  if (!first) return null;
  return rest.length ? `${first} (+${rest.length})` : first;
};

export type ServiceOrderItemRowValue = {
  /** Klientský klíč řádku; `id` ze serveru nejde použít, zápis řádky nahrazuje celé. */
  key: number;
  priceListItemId: string;
  quantity: string;
  isDataOrderLinked: boolean;
  dataOrder: DataOrderValue | null;
  /** Prázdný znamená, že se na dokladu tiskne název z ceníku. */
  documentItemName: string;
};

export const isQuantityValid = (quantity: string): boolean =>
  quantity !== '' && Number(quantity) > 0;

export const isRowValid = (row: ServiceOrderItemRowValue): boolean =>
  !!row.priceListItemId &&
  (row.isDataOrderLinked ? !!row.dataOrder : isQuantityValid(row.quantity));

/** Plochu navázaného řádku dopočítá server z objednávky a poslané množství odmítne. */
export const toItemRequest = (row: ServiceOrderItemRowValue): ServiceOrderItemEditRequest => {
  const documentItemName = row.documentItemName.trim();
  return {
    price_list_item: Number(row.priceListItemId),
    ...(documentItemName ? { price_list_item_name: documentItemName } : {}),
    ...(row.isDataOrderLinked
      ? { data_order: row.dataOrder?.id ?? null }
      : { quantity: row.quantity }),
  };
};

/** Název tištěný na dokladech: přepis z řádku, jinak název z ceníku (po potvrzení zmrazený). */
export const getItemName = (
  item: Pick<ServiceOrderItemResponse, 'price_list_item' | 'price_list_item_name'>,
): string => item.price_list_item_name ?? item.price_list_item.name;

/** Server přepis neoznačuje; v draftu ho pozná jen rozdíl proti živému `price_list_item.name`. */
export const getDocumentItemNameOverride = (
  item: Pick<ServiceOrderItemResponse, 'price_list_item_name'> & {
    price_list_item: Pick<ServiceOrderItemResponse['price_list_item'], 'name'>;
  },
): string =>
  item.price_list_item_name && item.price_list_item_name !== item.price_list_item.name
    ? item.price_list_item_name
    : '';

/** Datový řádek má `unit: null`; jeho množství je plocha v ha, což plyne jen z typu položky. */
export const formatItemQuantity = (
  formatMessage: FormatMessage,
  item: Pick<ServiceOrderItemResponse, 'price_list_item' | 'quantity' | 'unit'>,
): string =>
  isDataPriceListItemType(item.price_list_item.type)
    ? formatMessage(priceListMessages.quantityFallback, {
      quantity: Number(item.quantity),
      unit: formatMessage(serviceOrderMessages.hectareUnit),
    })
    : formatQuantityWithUnit(formatMessage, Number(item.quantity), item.unit);

/**
 * DUZP se předvyplňuje na dnešek. Přes `toISOString()` by to v ranních hodinách
 * spadlo o den zpátky (UTC), proto se datum skládá z lokálních složek.
 */
export const getTodayDate = (): string => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

type UserLike = {
  name?: string | null;
  surname?: string | null;
  company_name?: string | null;
};

/**
 * Popisek strany objednávky. Firmu nese jen `UserList` z našeptávače, na objednávce
 * je `NestedUser` bez ní — proto je pole volitelné a popisek se podle toho zkrátí.
 */
export const getUserLabel = (user?: UserLike | null): string => {
  if (!user) return '';
  const name = `${user.name ?? ''} ${user.surname ?? ''}`.trim();
  return user.company_name ? `${name} (${user.company_name})` : name;
};

/**
 * Prochází tělo chybové odpovědi a hledá v něm objekt s daným klíčem. Tvar 409
 * z `confirm` není v OpenAPI (endpoint deklaruje jen 200), takže se nedá typovat —
 * kód i cenu proto hledáme na kořeni i u polí.
 */
const visitErrorPayload = (
  node: unknown,
  visitor: (obj: Record<string, unknown>) => boolean,
): boolean => {
  if (Array.isArray(node)) return node.some((item) => visitErrorPayload(item, visitor));
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    if (visitor(obj)) return true;
    return Object.values(obj).some((value) => visitErrorPayload(value, visitor));
  }
  return false;
};

export const hasServerErrorCode = (payload: unknown, code: string): boolean =>
  visitErrorPayload(payload, (obj) => obj.code === code);

/** Aktuální cena z konfliktní odpovědi `confirm`, ať ji jde pracovnici ukázat. */
export const getCurrentPriceFromError = (payload: unknown): string | null => {
  let price: string | null = null;
  visitErrorPayload(payload, (obj) => {
    const value = obj.current_price;
    if (typeof value === 'string' || typeof value === 'number') {
      price = String(value);
      return true;
    }
    return false;
  });
  return price;
};

export type ItemRowServerErrors = Partial<
  Record<
    'price_list_item' | 'price_list_item_name' | 'quantity' | 'data_order' | 'non_field_errors',
    ServerError[]
  >
>;

/**
 * Chyby řádků chodí jako pole stejně dlouhé jako odeslané `items` (platný řádek je `{}`).
 * Chyba celého seznamu má jiný tvar: `ServerError[]`, nebo `{ non_field_errors }`.
 */
export const splitItemsServerErrors = (
  itemsErrors: unknown,
): { rows: ItemRowServerErrors[]; list: ServerError[] } => {
  if (!itemsErrors || typeof itemsErrors !== 'object') return { rows: [], list: [] };

  if (!Array.isArray(itemsErrors)) {
    const list = (itemsErrors as { non_field_errors?: ServerError[] }).non_field_errors;
    return { rows: [], list: list ?? [] };
  }

  const isListError = itemsErrors.some((error) => error && ('code' in error || 'message' in error));
  return isListError ? { rows: [], list: itemsErrors } : { rows: itemsErrors, list: [] };
};
